import React, { useState, useEffect, useRef } from 'react';
import { 
  Play, 
  Pause, 
  Volume2, 
  VolumeX, 
  Maximize, 
  Minimize, 
  MessageCircle, 
  ShoppingCart, 
  Users, 
  Eye,
  Share2,
  Settings,
  Facebook,
  Youtube,
  Link as LinkIcon
} from 'lucide-react';
import { ToastContext } from '../ToastProvider';
import LiveChat from './LiveChat';
import LiveShoppingCart from './LiveShoppingCart';

export default function LiveSellingPlatform({ affiliateId }) {
  const { notify } = React.useContext(ToastContext);
  const [isLive, setIsLive] = useState(false);
  const [streamType, setStreamType] = useState('facebook'); // 'facebook', 'youtube', 'custom'
  const [streamUrl, setStreamUrl] = useState('');
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [showChat, setShowChat] = useState(true);
  const [showProducts, setShowProducts] = useState(true);
  const [streamError, setStreamError] = useState(null);
  const [isStreamLoading, setIsStreamLoading] = useState(false);
  const [currentEmbedMethod, setCurrentEmbedMethod] = useState(0);
  const [embedAttempts, setEmbedAttempts] = useState(0);
  const [messageCount, setMessageCount] = useState(0);
  const [cartItems, setCartItems] = useState([]);
  const [cartTotal, setCartTotal] = useState(0);
  const [cartItemCount, setCartItemCount] = useState(0);
  
  const videoRef = useRef(null);
  const chatRef = useRef(null);

  // Mock viewer count simulation
  useEffect(() => {
    if (isLive) {
      const interval = setInterval(() => {
        setViewerCount(prev => prev + Math.floor(Math.random() * 3) - 1);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [isLive]);


  const startLiveStream = () => {
    if (!streamUrl) {
      notify('Please enter a stream URL', { type: 'error' });
      return;
    }

    // Validate URL format
    if (streamType === 'facebook' && !streamUrl.includes('facebook.com')) {
      notify('Please enter a valid Facebook Live URL', { type: 'error' });
      return;
    }

    if (streamType === 'youtube' && !streamUrl.includes('youtube.com') && !streamUrl.includes('youtu.be')) {
      notify('Please enter a valid YouTube Live URL', { type: 'error' });
      return;
    }

    setIsStreamLoading(true);
    setStreamError(null);
    setIsLive(true);
    setViewerCount(Math.floor(Math.random() * 50) + 10);
    notify('Live stream started!', { type: 'success' });
    
    // Simulate loading time
    setTimeout(() => {
      setIsStreamLoading(false);
    }, 2000);
  };

  const stopLiveStream = () => {
    setIsLive(false);
    setViewerCount(0);
    setStreamError(null);
    setIsStreamLoading(false);
    notify('Live stream ended', { type: 'info' });
  };

  const handleStreamError = (error) => {
    setStreamError(error);
    setIsStreamLoading(false);
    notify('Stream error occurred. Try refreshing or using "Open in New Tab".', { type: 'error' });
  };

  const retryStream = () => {
    setStreamError(null);
    setIsStreamLoading(true);
    setTimeout(() => {
      setIsStreamLoading(false);
    }, 2000);
  };


  const handleCartUpdate = (items, total, count) => {
    setCartItems(items);
    setCartTotal(total);
    setCartItemCount(count);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      videoRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const getEmbedUrl = () => {
    if (streamType === 'facebook') {
      // Handle different Facebook Live URL formats
      if (streamUrl.includes('facebook.com/')) {
        return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(streamUrl)}&show_text=false&width=100%&height=100%`;
      }
      return streamUrl;
    } else if (streamType === 'youtube') {
      // Extract video ID from various YouTube URL formats
      let videoId = '';
      if (streamUrl.includes('youtube.com/watch?v=')) {
        videoId = streamUrl.split('v=')[1].split('&')[0];
      } else if (streamUrl.includes('youtu.be/')) {
        videoId = streamUrl.split('youtu.be/')[1].split('?')[0];
      } else if (streamUrl.includes('youtube.com/embed/')) {
        videoId = streamUrl.split('embed/')[1].split('?')[0];
      }
      
      if (videoId) {
        // Try multiple embedding methods
        return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=0&rel=0&modestbranding=1&fs=1&controls=1&enablejsapi=1&origin=${window.location.origin}`;
      }
    }
    return streamUrl;
  };

  const getAlternativeEmbedMethods = () => {
    if (streamType !== 'youtube') return [];
    
    let videoId = '';
    if (streamUrl.includes('youtube.com/watch?v=')) {
      videoId = streamUrl.split('v=')[1].split('&')[0];
    } else if (streamUrl.includes('youtu.be/')) {
      videoId = streamUrl.split('youtu.be/')[1].split('?')[0];
    }
    
    if (!videoId) return [];
    
    return [
      {
        name: 'Standard Embed',
        url: `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=0&rel=0&modestbranding=1&fs=1&controls=1&enablejsapi=1&origin=${window.location.origin}`,
        description: 'Official YouTube embed with full controls'
      },
      {
        name: 'No Cookie Embed',
        url: `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&mute=0&rel=0&modestbranding=1&fs=1&controls=1&enablejsapi=1&origin=${window.location.origin}`,
        description: 'YouTube embed without cookies (often works better)'
      },
      {
        name: 'Minimal Embed',
        url: `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=0&rel=0&modestbranding=1&fs=0&controls=1&showinfo=0&iv_load_policy=3`,
        description: 'Minimal YouTube embed with fewer restrictions'
      },
      {
        name: 'Live Stream Embed',
        url: `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=0&rel=0&modestbranding=1&fs=1&controls=1&enablejsapi=1&origin=${window.location.origin}&live=1`,
        description: 'Optimized for live streams'
      }
    ];
  };

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-3 h-3 rounded-full ${isLive ? 'bg-red-400 animate-pulse' : 'bg-gray-400'}`}></div>
            <h2 className="text-xl font-bold">Live Selling Platform</h2>
            {isLive && (
              <div className="flex items-center space-x-4 text-sm">
                <div className="flex items-center space-x-1">
                  <Eye className="w-4 h-4" />
                  <span>{viewerCount} viewers</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Users className="w-4 h-4" />
                  <span>{messageCount} messages</span>
                </div>
                <div className="flex items-center space-x-1">
                  <ShoppingCart className="w-4 h-4" />
                  <span>{cartItemCount} items</span>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowChat(!showChat)}
              className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
            </button>
            <button
              onClick={() => setShowProducts(!showProducts)}
              className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
            >
              <ShoppingCart className="w-5 h-5" />
            </button>
            <button className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors">
              <Share2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex h-96">
        {/* Main Video Area */}
        <div className="flex-1 relative bg-black">
          {isLive ? (
            <div className="relative w-full h-full">
              {isStreamLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-white">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                    <p className="text-lg font-medium">Loading stream...</p>
                    <p className="text-sm text-gray-300">Please wait while we connect to your live stream</p>
                  </div>
                </div>
              ) : streamError ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-white">
                    <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl">⚠️</span>
                    </div>
                    <h3 className="text-xl font-semibold mb-2">Stream Error</h3>
                    <p className="text-gray-300 mb-4">Unable to load the live stream</p>
                    <div className="space-x-3">
                      <button
                        onClick={retryStream}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                      >
                        Retry
                      </button>
                      <button
                        onClick={() => window.open(streamUrl, '_blank')}
                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                      >
                        Open in New Tab
                      </button>
                    </div>
                  </div>
                </div>
              ) : streamType === 'facebook' ? (
                <iframe
                  src={getEmbedUrl()}
                  className="w-full h-full"
                  frameBorder="0"
                  allowFullScreen
                  title="Facebook Live Stream"
                  onError={() => handleStreamError('Facebook stream failed to load')}
                />
              ) : streamType === 'youtube' ? (
                <div className="relative w-full h-full">
                  {embedAttempts < getAlternativeEmbedMethods().length ? (
                    <div className="relative w-full h-full">
                      <iframe
                        key={`${currentEmbedMethod}-${embedAttempts}`}
                        src={getAlternativeEmbedMethods()[currentEmbedMethod]?.url}
                        className="w-full h-full"
                        frameBorder="0"
                        allowFullScreen
                        title={`YouTube Live Stream - Method ${currentEmbedMethod + 1}`}
                        onError={() => {
                          const nextMethod = (currentEmbedMethod + 1) % getAlternativeEmbedMethods().length;
                          setCurrentEmbedMethod(nextMethod);
                          setEmbedAttempts(prev => prev + 1);
                          if (nextMethod === 0 && embedAttempts >= getAlternativeEmbedMethods().length - 1) {
                            handleStreamError('All YouTube embedding methods failed');
                          }
                        }}
                        onLoad={() => {
                          // If iframe loads successfully, reset attempts
                          setEmbedAttempts(0);
                        }}
                      />
                      
                      {/* Embed Method Selector */}
                      <div className="absolute top-4 left-4">
                        <div className="bg-black/70 text-white p-2 rounded-lg">
                          <p className="text-xs mb-1">Method: {getAlternativeEmbedMethods()[currentEmbedMethod]?.name}</p>
                          <p className="text-xs text-gray-300">{getAlternativeEmbedMethods()[currentEmbedMethod]?.description}</p>
                        </div>
                      </div>
                      
                      {/* Try Different Method Button */}
                      <div className="absolute top-4 right-4">
                        <button
                          onClick={() => {
                            const nextMethod = (currentEmbedMethod + 1) % getAlternativeEmbedMethods().length;
                            setCurrentEmbedMethod(nextMethod);
                            setEmbedAttempts(prev => prev + 1);
                          }}
                          className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                        >
                          Try Different Method
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Fallback when all methods fail */
                    <div className="flex items-center justify-center h-full bg-gradient-to-br from-red-900 to-red-800">
                      <div className="text-center text-white p-8">
                        <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
                          <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                          </svg>
                        </div>
                        <h3 className="text-2xl font-bold mb-4">YouTube Live Stream</h3>
                        <p className="text-lg mb-6 text-red-100">
                          All embedding methods failed for this stream.<br/>
                          Click below to watch in a new tab while using our chat and products.
                        </p>
                        <div className="space-y-3">
                          <button
                            onClick={() => window.open(streamUrl, '_blank')}
                            className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center space-x-2"
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                            </svg>
                            <span>Watch on YouTube</span>
                          </button>
                          <button
                            onClick={() => {
                              setCurrentEmbedMethod(0);
                              setEmbedAttempts(0);
                            }}
                            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            Try Embedding Again
                          </button>
                          <p className="text-sm text-red-200">
                            💡 You can still use the chat and products below while watching!
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <video
                  ref={videoRef}
                  src={streamUrl}
                  className="w-full h-full object-cover"
                  controls
                  autoPlay
                  muted={isMuted}
                  onError={() => handleStreamError('Video stream failed to load')}
                />
              )}
              
              {/* Stream Controls Overlay */}
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setIsMuted(!isMuted)}
                    className="p-2 bg-black/50 text-white rounded-lg hover:bg-black/70 transition-colors"
                  >
                    {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={toggleFullscreen}
                    className="p-2 bg-black/50 text-white rounded-lg hover:bg-black/70 transition-colors"
                  >
                    {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
                  </button>
                </div>
                
                <button
                  onClick={stopLiveStream}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
                >
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span>End Stream</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-white">
                <div className="mb-4">
                  <Play className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <h3 className="text-xl font-semibold mb-2">Start Your Live Stream</h3>
                  <p className="text-gray-300 mb-6">Connect your Facebook Live or YouTube Live stream</p>
                </div>
                
                {/* Stream Setup */}
                <div className="max-w-md mx-auto space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Stream Platform</label>
                    <select
                      value={streamType}
                      onChange={(e) => setStreamType(e.target.value)}
                      className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white"
                    >
                      <option value="facebook">Facebook Live</option>
                      <option value="youtube">YouTube Live</option>
                      <option value="custom">Custom Stream URL</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Stream URL</label>
                    <input
                      type="url"
                      value={streamUrl}
                      onChange={(e) => setStreamUrl(e.target.value)}
                      placeholder={
                        streamType === 'facebook' 
                          ? 'https://www.facebook.com/yourpage/live'
                          : streamType === 'youtube'
                          ? 'https://www.youtube.com/watch?v=VIDEO_ID'
                          : 'https://your-stream-url.com'
                      }
                      className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400"
                    />
                    {streamType === 'youtube' && (
                      <p className="text-xs text-yellow-300 mt-1">
                        🎯 Platform will try 4 different embedding methods automatically. If all fail, external viewing is available.
                      </p>
                    )}
                  </div>
                  
                  <button
                    onClick={startLiveStream}
                    className="w-full py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center space-x-2"
                  >
                    <Play className="w-5 h-5" />
                    <span>Start Live Stream</span>
                  </button>
                  
                  {/* Troubleshooting Tips */}
                  <div className="mt-4 p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                    <h4 className="text-sm font-medium text-blue-200 mb-2">💡 YouTube Embedding Methods</h4>
                    <ul className="text-xs text-blue-100 space-y-1">
                      <li>• <strong>Standard Embed:</strong> Official YouTube embed with full controls</li>
                      <li>• <strong>No Cookie Embed:</strong> Often works better, bypasses some restrictions</li>
                      <li>• <strong>Minimal Embed:</strong> Fewer features, fewer restrictions</li>
                      <li>• <strong>Live Stream Embed:</strong> Optimized specifically for live streams</li>
                      <li>• <strong>Tip:</strong> Platform will try all methods automatically!</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Enhanced Live Chat */}
        {showChat && (
          <LiveChat
            sessionId={`live-${affiliateId}-${Date.now()}`}
            affiliateId={affiliateId}
            isLive={isLive}
            onMessageCountChange={setMessageCount}
          />
        )}
      </div>

      {/* Live Shopping Cart */}
      {showProducts && (
        <LiveShoppingCart
          sessionId={`live-${affiliateId}-${Date.now()}`}
          affiliateId={affiliateId}
          isLive={isLive}
          onCartUpdate={handleCartUpdate}
        />
      )}
    </div>
  );
}
